import * as THREE from 'three';
import {
  createContext,
  useContext,
  type ReactNode,
  useState,
  useCallback,
  useRef,
  useEffect,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from 'react';
import { NavigationControls } from './Navigation';
import type {
  AbyssViewerCallbacks,
  AnimationStepProps,
  HoveredInstance,
  OctreeGroups,
  SharedGeometries,
  TransformTarget,
} from '../types';
import { DraggingHelper } from './DraggingHelper';
import { useSharedGeometries } from './useSharedGeometries';
import MeshPickHelper from './MeshPickHelper';
import { HybridRaycaster } from './HybridRaycaster';
import PickHelper from './PickHelper';
import { CameraType, UnitSystemEnum } from './Enums';
import { Canvas, type CanvasProps as FiberCanvasProps } from '@react-three/fiber';
import { AnimationEditor } from './Animation/AnimationEditor';
import { hasMajorPerformanceCaveat } from '../helpers/hasMajorPerformanceCaveat';

const performanceProperty = { min: 0.85 };

const style: React.CSSProperties = { width: '100%', height: '100%', top: 80 };

const animationWrapperDivStyle: React.CSSProperties = {
  position: 'absolute',
  right: '10px',
  top: 40,
  margin: '10px',
  zIndex: 16777272, // greater than defeault max z index of HtmlSvgMarker
};

export type CanvasProps = Omit<FiberCanvasProps, 'children'>;

export interface AbyssViewerContextValue {
  callbacks: AbyssViewerCallbacks | undefined;
  setCallbacks: (callbacks: AbyssViewerCallbacks) => void;

  navigationControls: NavigationControls | undefined;
  setNavigationControls: (navigationControls: NavigationControls) => void;
  draggingHelper: DraggingHelper | undefined;
  setDraggingHelper: (draggingHelper: DraggingHelper) => void;

  addTransformTarget: (name: string, target: TransformTarget) => void;
  removeTransformTarget: (name: string) => void;
  getTransformTarget: (name: string) => TransformTarget | undefined;

  sharedGeometries: SharedGeometries | undefined;

  hybridRaycaster: HybridRaycaster | undefined;

  pickHelper: PickHelper | undefined;
  meshPickHelper: MeshPickHelper | undefined;

  octreeGroups: OctreeGroups | undefined;

  hoveredInstance: HoveredInstance | undefined;
  setHoveredInstance: Dispatch<SetStateAction<HoveredInstance | undefined>> | undefined;

  clippingPlanes: THREE.Plane[] | undefined;

  unitSystem: UnitSystemEnum | undefined;
  setUnitSystem: (unitSystem: UnitSystemEnum) => void;

  drawBoxReference: React.RefObject<HTMLDivElement> | undefined;
  overlayNode: ReactNode | undefined;
  setOverlayNode: (overlayNode: ReactNode | undefined) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AbyssViewerContext = createContext<AbyssViewerContextValue>({
  callbacks: undefined,
  setCallbacks: () => {},

  navigationControls: undefined,
  setNavigationControls: () => {},
  draggingHelper: undefined,
  setDraggingHelper: () => {},

  addTransformTarget: () => {},
  removeTransformTarget: () => {},
  getTransformTarget: () => undefined,

  sharedGeometries: undefined,

  hybridRaycaster: undefined,

  pickHelper: undefined,
  meshPickHelper: undefined,

  octreeGroups: undefined,

  hoveredInstance: undefined,
  setHoveredInstance: undefined,

  clippingPlanes: undefined,

  unitSystem: undefined,
  setUnitSystem: () => {},

  drawBoxReference: undefined,
  overlayNode: undefined,
  setOverlayNode: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAbyssViewerContext = (): AbyssViewerContextValue => useContext(AbyssViewerContext);

export type CameraProps = {
  cameraType: CameraType;
  position?: Parameters<THREE.Vector3['set']>;
  up?: Parameters<THREE.Vector3['set']>;
  fov?: number;
  near?: number;
  far?: number;
};

export type AbyssViewerContextProviderProps = {
  canvasProps: CanvasProps;
  onMajorPerformanceCaveat?: () => void;
  cameraProps?: CameraProps;
  displayAnimationEditor?: boolean;
  initialAnimationSteps?: AnimationStepProps[];
  children?: ReactNode;
};

export const AbyssViewerContextProvider = ({
  canvasProps,
  onMajorPerformanceCaveat,
  cameraProps,
  displayAnimationEditor,
  initialAnimationSteps,
  children,
}: AbyssViewerContextProviderProps) => {
  const [callbacks, setCallbacks] = useState<AbyssViewerCallbacks | undefined>(undefined);

  const [draggingHelper, setDraggingHelper] = useState<DraggingHelper>();

  const [navigationControls, setNavigationControls] = useState<NavigationControls>();

  const transformTargetsReference = useRef(new Map<string, TransformTarget>());
  const transformTargets = transformTargetsReference.current;

  const addTransformTarget = useCallback(
    (name: string, target: TransformTarget) => {
      transformTargets.set(name, target);
    },
    [transformTargets]
  );
  const removeTransformTarget = useCallback(
    (name: string) => {
      transformTargets.delete(name);
    },
    [transformTargets]
  );

  const getTransformTarget = useCallback(
    (name: string) => transformTargets.get(name),
    [transformTargets]
  );

  const sharedGeometries = useSharedGeometries();

  const [hybridRaycaster, setHybridRaycaster] = useState<HybridRaycaster>();

  const [pickHelper, setPickHelper] = useState<PickHelper>();
  const [meshPickHelper, setMeshPickHelper] = useState<MeshPickHelper>();

  useEffect(() => {
    setHybridRaycaster(new HybridRaycaster());
    setMeshPickHelper(new MeshPickHelper());
  }, []);

  useEffect(() => {
    setPickHelper(new PickHelper(sharedGeometries.viewerSphere));
  }, [sharedGeometries]);

  useEffect(() => {
    return () => meshPickHelper?.dispose();
  }, [meshPickHelper]);

  useEffect(() => {
    return () => pickHelper?.dispose();
  }, [pickHelper]);

  const [octreeGroups, setOctreeGroups] = useState<OctreeGroups>();

  useEffect(() => {
    setOctreeGroups(new Map<string, { group: THREE.Group | null; matrix: THREE.Matrix4 }>());
  }, []);

  useEffect(() => {
    if (pickHelper && octreeGroups) {
      pickHelper.setOctreeGroups(octreeGroups);
    }
  }, [pickHelper, octreeGroups]);

  const [hoveredInstance, setHoveredInstance] = useState<HoveredInstance>();

  const clippingPlanes = useRef(
    [...Array(6).keys()].map((_, index) => {
      const normal = [0, 0, 0];
      const sign = index & 1;
      const axis = Math.trunc(index / 2);
      normal[axis] = sign === 0 ? -1 : 1;

      return new THREE.Plane(new THREE.Vector3(...normal), 0.1);
    })
  );

  const [unitSystem, setUnitSystem] = useState<UnitSystemEnum>(UnitSystemEnum.METRIC);

  // const rootElementReference = useRef<HTMLDivElement>(null);
  const drawBoxReference = useRef<HTMLDivElement>(null);

  const [overlayNode, setOverlayNode] = useState<ReactNode | undefined>(undefined);

  const cameraPropsMemo = useMemo(
    () => ({
      position: cameraProps?.position ?? [0, 100, 0],
      fov: cameraProps?.fov ?? 50,
      up: cameraProps?.up ?? [0, 0, 1],
      near: cameraProps?.near,
      far: cameraProps?.far,
    }),
    [cameraProps]
  );

  useEffect(() => {
    if (onMajorPerformanceCaveat && hasMajorPerformanceCaveat()) {
      onMajorPerformanceCaveat();
    }
  }, [onMajorPerformanceCaveat]);

  return (
    <AbyssViewerContext.Provider
      value={{
        callbacks,
        setCallbacks,

        navigationControls,
        setNavigationControls,
        draggingHelper,
        setDraggingHelper,

        addTransformTarget,
        removeTransformTarget,
        getTransformTarget,

        sharedGeometries,

        hybridRaycaster,

        pickHelper,
        meshPickHelper,

        octreeGroups,

        hoveredInstance,
        setHoveredInstance,

        clippingPlanes: clippingPlanes.current,

        unitSystem,
        setUnitSystem,

        drawBoxReference,
        overlayNode,
        setOverlayNode,
      }}
    >
      {overlayNode}
      <div style={style}>
        <div ref={drawBoxReference} />
        <Canvas
          {...canvasProps}
          flat
          // shadows
          frameloop="demand"
          orthographic={cameraProps?.cameraType === CameraType.Orthographic}
          camera={cameraPropsMemo}
          performance={performanceProperty}
        >
          {children}
        </Canvas>
        {displayAnimationEditor && (
          <div style={animationWrapperDivStyle}>
            <AnimationEditor
              // abyssViewer={abyssComponentReference}
              initialAnimationSteps={initialAnimationSteps}
            />
          </div>
        )}
      </div>
    </AbyssViewerContext.Provider>
  );
};
